import type { FC } from "react";

import styled from "styled-components";

import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";

import { useTranslation } from "../../WithTranslation";
import {
    type AutocompleteDemandAnalysisItem,
    type AutocompleteDemandAnalysisEntity,
    AutocompleteDemandAnalysisEntityType,
    isKeywordGroupItem,
} from "../types/AutocompleteDemandAnalysisTypes";

import { AutocompleteDemandAnalysisEmptyState } from "./AutocompleteDemandAnalysisEmptyState";
import { AutocompleteDemandAnalysisListItem } from "./AutocompleteDemandAnalysisListItem";
import { AutocompleteDemandAnalysisSectionHeader } from "./AutocompleteDemandAnalysisSectionHeader";

const ItemsContainer = styled.div`
    width: 100%;
    max-height: 300px;
    overflow: auto;
    display: flex;
    flex-direction: column;
`;

interface IAutocompleteDemandAnalysisContentProps {
    data: AutocompleteDemandAnalysisItem[];
    selectedIndex: number;
    query: string;
    onClick?: (entity: AutocompleteDemandAnalysisEntity) => void;
    onItemNavigation: (item: AutocompleteDemandAnalysisItem) => void;
    handleGroupEditClick: (item: IUserKeywordGroup) => void;
    handleGroupShareClick: (item: IUserKeywordGroup) => void;
    showEditButton: boolean;
    showShareButton: boolean;
    sectionTitle?: string;
    isFocused: boolean;
    isLoading: boolean;
}

export const AutocompleteDemandAnalysisContent: FC<IAutocompleteDemandAnalysisContentProps> = ({
    data,
    selectedIndex,
    query,
    onClick,
    onItemNavigation,
    handleGroupEditClick,
    handleGroupShareClick,
    showEditButton,
    showShareButton,
    sectionTitle,
    isFocused,
    isLoading,
}) => {
    const t = useTranslation();
    const hasQuery = query && query.trim().length >= 2;
    const hasData = data && data.length > 0;

    if (!isFocused) {
        return null;
    }

    if (!hasData && hasQuery && isLoading) {
        return null;
    }

    if (!hasData && hasQuery) {
        return (
            <div className="ListItemsContainer">
                <AutocompleteDemandAnalysisEmptyState />
            </div>
        );
    }

    if (!hasData) {
        return null;
    }

    const getSubtitle = (item: AutocompleteDemandAnalysisItem): string | undefined => {
        switch (item.type) {
            case AutocompleteDemandAnalysisEntityType.ownList:
                return t("demand.analysis.autocomplete.ownList");
            case AutocompleteDemandAnalysisEntityType.sharedList:
                return t("demand.analysis.autocomplete.sharedList");
            default:
                return undefined;
        }
    };

    const handleItemClick = (item: AutocompleteDemandAnalysisItem, e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();

        if (onClick) {
            onClick(item.originalData);
        }

        onItemNavigation(item);
    };

    const handleEditClick = (item: AutocompleteDemandAnalysisItem) => {
        if (isKeywordGroupItem(item)) {
            // Convert the unified item back to the expected format for the modal
            const keywordGroup = item.originalData;
            handleGroupEditClick(keywordGroup);
        }
    };

    const handleShareClick = (item: AutocompleteDemandAnalysisItem) => {
        if (isKeywordGroupItem(item)) {
            // Convert the unified item back to the expected format for the modal
            const keywordGroup = item.originalData;
            handleGroupShareClick(keywordGroup);
        }
    };

    const renderListItem = (item: AutocompleteDemandAnalysisItem, index: number) => (
        <AutocompleteDemandAnalysisListItem
            key={`${item.type}-${item.id}-${item.name}`}
            query={query}
            title={item.name}
            subtitle={getSubtitle(item)}
            isActive={selectedIndex === index}
            onClick={(e) => handleItemClick(item, e)}
            iconName={item.icon}
            item={item}
            onEditClick={handleEditClick}
            onShareClick={handleShareClick}
            showEditButton={showEditButton}
            showShareButton={showShareButton}
        />
    );

    // When there's no query, show simple list with optional section title (recents)
    if (!hasQuery) {
        return (
            <ItemsContainer className="ListItemsContainer">
                {sectionTitle ? <AutocompleteDemandAnalysisSectionHeader title={sectionTitle} /> : null}
                {data.map((item, index) => renderListItem(item, index))}
            </ItemsContainer>
        );
    }

    // When there's a query, group items by type with section headers
    const keywordGroupItems = data.filter((item) => isKeywordGroupItem(item));
    const topicItems = data.filter((item) => item.type === AutocompleteDemandAnalysisEntityType.aiTopic);

    // Calculate the correct index offset for topic items
    const keywordGroupItemsCount = keywordGroupItems.length;
    const hasKeywordGroups = keywordGroupItems.length > 0;
    const hasTopics = topicItems.length > 0;

    const showSectionHeaders = hasKeywordGroups && hasTopics;

    return (
        <ItemsContainer className="ListItemsContainer">
            {hasKeywordGroups ? (
                <>
                    {showSectionHeaders ? (
                        <AutocompleteDemandAnalysisSectionHeader
                            title={t("demand_analysis.autocomplete.section.saved_lists.heading")}
                        />
                    ) : null}
                    {keywordGroupItems.map((item, index) => renderListItem(item, index))}
                </>
            ) : null}
            {hasTopics ? (
                <>
                    {showSectionHeaders ? (
                        <AutocompleteDemandAnalysisSectionHeader
                            title={t("demand_analysis.autocomplete.section.topics.heading")}
                        />
                    ) : null}
                    {topicItems.map((item, index) => renderListItem(item, keywordGroupItemsCount + index))}
                </>
            ) : null}
        </ItemsContainer>
    );
};
